import type { FC, ReactNode } from 'react'

import { useRouter } from 'next/router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { CtaBand } from '@/components/generals/cta-band'
import { Landscape } from '@/components/generals/landscape'
import { PageHero } from '@/components/generals/page-hero'
import { PillButton } from '@/components/generals/pill-button'
import { Reveal } from '@/components/generals/reveal'
import { LANDSCAPES } from '@/lib/visual'

import {
    LoanPayoffCalculator,
    NetWorthCalculator,
    RetirementCalculator,
    SavingsGoalCalculator
} from '@/components/landing/tools/calculators'

type ToolId = 'savings' | 'retirement' | 'loan' | 'net-worth'

interface Tool {
    id: ToolId
    name: string
    title: string
    description: string
    image: string
    position: string
    spark: Array<number>
    render: () => ReactNode
}

const TOOLS: Array<Tool> = [
    {
        id: 'savings',
        name: 'Savings goal',
        title: 'Saving for something',
        description: 'When you will get there, and what would bring the day closer.',
        image: LANDSCAPES.wildflowers,
        position: 'center 60%',
        spark: [8, 12, 15, 21, 26, 33, 41, 50],
        render: () => <SavingsGoalCalculator />
    },
    {
        id: 'retirement',
        name: 'Retirement',
        title: 'Life after work',
        description: 'What your nest egg could grow into, and the income it might provide.',
        image: LANDSCAPES.goldenTree,
        position: 'center 50%',
        spark: [6, 8, 11, 15, 21, 29, 39, 52],
        render: () => <RetirementCalculator />
    },
    {
        id: 'loan',
        name: 'Loan payoff',
        title: 'Clearing a loan',
        description: 'Find your debt-free date and what a little extra each month saves.',
        image: LANDSCAPES.windingPath,
        position: 'center 60%',
        spark: [50, 44, 38, 31, 24, 17, 9, 2],
        render: () => <LoanPayoffCalculator />
    },
    {
        id: 'net-worth',
        name: 'Net worth',
        title: 'Your whole picture',
        description: 'What you own less what you owe, as it stands now and ten years on.',
        image: LANDSCAPES.hillTree,
        position: 'center 45%',
        spark: [20, 22, 21, 26, 30, 29, 36, 42],
        render: () => <NetWorthCalculator />
    }
]

const isToolId = (value: string): value is ToolId => TOOLS.some(tool => tool.id === value)

const Sparkline = ({ values }: { values: Array<number> }) => {
    const max = Math.max(...values)
    const points = values.map((value, index) => `${(index / (values.length - 1)) * 100},${40 - (value / max) * 36}`).join(' ')

    return (
        <svg viewBox='0 0 100 40' preserveAspectRatio='none' className='h-12 w-full' aria-hidden='true'>
            <polyline points={points} fill='none' stroke='#e2f37a' strokeWidth='2' vectorEffect='non-scaling-stroke' strokeLinecap='round' strokeLinejoin='round' />
        </svg>
    )
}

interface CalculatorDialogProps {
    tool: Tool
    onClose: () => void
}

const CalculatorDialog = ({ tool, onClose }: CalculatorDialogProps) => {
    const closeRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        const previous = document.body.style.overflow
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose()
        }

        document.body.style.overflow = 'hidden'
        window.addEventListener('keydown', onKey)
        closeRef.current?.focus()

        return () => {
            document.body.style.overflow = previous
            window.removeEventListener('keydown', onKey)
        }
    }, [onClose])

    return (
        <div role='dialog' aria-modal='true' aria-labelledby='calculator-title' className='fixed inset-0 z-[70] flex items-end justify-center sm:items-center sm:p-6'>
            <button
                type='button'
                aria-label='Close calculator'
                tabIndex={-1}
                onClick={onClose}
                className='absolute inset-0 cursor-default bg-black/75 backdrop-blur-md animate-in fade-in duration-500'
            />

            <div className='glass-dark grain relative max-h-[92svh] w-full max-w-5xl overflow-y-auto rounded-t-[30px] p-5 animate-in fade-in slide-in-from-bottom-8 duration-700 sm:rounded-[32px] sm:p-8'>
                <div className='flex items-start justify-between gap-4'>
                    <div>
                        <p className='text-[11px] font-medium uppercase tracking-[0.3em] text-cream/50'>
                            Calculator
                        </p>

                        <h2 id='calculator-title' className='mt-2 font-serif text-4xl italic text-cream sm:text-5xl'>
                            {tool.title}
                        </h2>

                        <p className='mt-2 text-sm text-cream/55'>
                            {tool.description}
                        </p>
                    </div>

                    <button
                        ref={closeRef}
                        type='button'
                        onClick={onClose}
                        aria-label='Close calculator'
                        className='glass grid size-11 shrink-0 place-items-center rounded-full text-cream transition-transform duration-300 hover:rotate-90'
                    >
                        <X className='size-5' />
                    </button>
                </div>

                <div className='mt-8'>
                    {tool.render()}
                </div>

                <p className='mt-8 text-xs leading-relaxed text-cream/40'>
                    A guide, not a promise. These figures rest on simple, steady assumptions and are not financial advice. Actual returns, rates and taxes will differ.
                </p>
            </div>
        </div>
    )
}

const ToolsPage: FC = (): ReactNode => {
    const router = useRouter()
    const [active, setActive] = useState<ToolId | null>(null)
    const activeRef = useRef<ToolId | null>(null)

    activeRef.current = active

    useEffect(() => {
        const fromHash = () => {
            const id = window.location.hash.replace('#', '')

            if (isToolId(id)) setActive(id)
        }

        fromHash()
        router.events.on('hashChangeComplete', fromHash)

        return () => router.events.off('hashChangeComplete', fromHash)
    }, [router.events])

    const open = (id: ToolId) => {
        setActive(id)
        window.history.replaceState(window.history.state, '', `#${id}`)
    }

    const close = useCallback(() => {
        const id = activeRef.current

        setActive(null)
        window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search)

        if (id) requestAnimationFrame(() => document.getElementById(id)?.querySelector<HTMLButtonElement>('button')?.focus())
    }, [])

    const tool = TOOLS.find(item => item.id === active)

    return (
        <Layout>
            <Seo
                title='Money tools'
                description='Free calculators for savings goals, retirement, paying off a loan and net worth. Slide the inputs and see the figures and charts respond at once.'
            />

            <PageHero
                eyebrow='Money tools'
                title='Plain figures for *weighty choices*'
                subtitle='Four quiet calculators for the questions that come up in every life. Slide an input and see the answer shift, with no account required.'
                image={LANDSCAPES.seedHeads}
                position='center 60%'
            />

            <section aria-label='Calculators' className='pb-28'>
                <ul className='shell grid gap-5 sm:grid-cols-2'>
                    {TOOLS.map((item, index) => (
                        <Reveal as='li' key={item.id} id={item.id} delay={(index % 2) * 140}>
                            <article className='grain group relative isolate flex min-h-[420px] cursor-pointer flex-col justify-between overflow-hidden rounded-[32px] bg-ink p-6 shadow-[0_0_0_0_rgba(226,243,122,0)] transition-shadow duration-1000 ease-cinema hover:shadow-[0_40px_90px_-40px_rgba(244,170,100,0.55)] sm:p-8'
                                onClick={() => open(item.id)}>
                                <Landscape
                                    src={item.image}
                                    fade='card'
                                    position={item.position}
                                    sizes='(min-width: 640px) 50vw, 100vw'
                                    imageClassName='scale-[1.02] group-hover:scale-[1.08] group-hover:brightness-[.9]'
                                    className='-z-10'
                                />
                                <div aria-hidden='true' className='hairline pointer-events-none absolute inset-0 rounded-[inherit]' />

                                <div className='glass max-w-[260px] rounded-[22px] p-4'>
                                    <p className='text-[11px] uppercase tracking-[0.22em] text-cream/55'>
                                        {item.name}
                                    </p>

                                    <Sparkline values={item.spark} />
                                </div>

                                <div className='mt-10'>
                                    <h2 className='font-serif text-4xl italic leading-tight text-cream'>
                                        {item.title}
                                    </h2>

                                    <p className='mt-2 max-w-sm text-sm leading-relaxed text-cream/65 transition-colors duration-500 group-hover:text-cream/85'>
                                        {item.description}
                                    </p>

                                    <PillButton
                                        className='mt-6'
                                        onClick={() => open(item.id)}
                                        ariaLabel={`Open the ${item.name.toLowerCase()} calculator`}
                                    >
                                        Try it now
                                    </PillButton>
                                </div>
                            </article>
                        </Reveal>
                    ))}
                </ul>
            </section>

            {tool && <CalculatorDialog tool={tool} onClose={close} />}

            <CtaBand
                eyebrow='Take the next step'
                title={'Figures sketch the path.\n*An advisor* walks it with you.'}
                buttonLabel='Meet an advisor'
                href='/find-an-advisor'
                note='The first call with a Verdane advisor costs nothing.'
                image='/images/uploads/replace-c.png'
                position='center 35%'
            />
        </Layout>
    )
}

export default ToolsPage
